// DOM-free helpers for the select controller, kept separate so they can be unit tested in node.

export function normalize(text) {
  return String(text ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLocaleLowerCase()
}

export function searchTerms(query) {
  return normalize(query).split(/\s+/).filter(Boolean)
}

// Every term must appear somewhere in the label, in any order ("mül ann" finds "Anna Müller").
export function matches(label, terms) {
  const haystack = normalize(label)
  return terms.every(term => haystack.includes(term))
}

// Splits a label into [{ text, match }] parts so matches can be highlighted without parsing HTML.
// Works on the normalized label, which has the same length as the original for precomposed text.
export function highlightParts(label, terms) {
  const text = String(label ?? "")
  const haystack = normalize(text)
  if (terms.length === 0 || haystack.length !== text.length) return [{ text, match: false }]

  const marked = new Array(text.length).fill(false)
  terms.forEach(term => {
    let from = 0
    let index
    while (term && (index = haystack.indexOf(term, from)) !== -1) {
      marked.fill(true, index, index + term.length)
      from = index + term.length
    }
  })

  const parts = []
  for (let i = 0; i < text.length; i++) {
    const last = parts[parts.length - 1]
    if (last && last.match === marked[i]) {
      last.text += text[i]
    } else {
      parts.push({ text: text[i], match: marked[i] })
    }
  }
  return parts
}

export function initials(label) {
  return String(label ?? "").trim().split(/\s+/).slice(0, 2).map(word => word[0] || "").join("").toUpperCase()
}

// A blank option (empty value and label) only exists so the form can submit "", it is never listed.
export function isBlank(option) {
  return option.value === "" && option.label.trim() === ""
}

// Next enabled index in a direction, wrapping around. Returns -1 when nothing is enabled.
export function nextEnabledIndex(items, current, step) {
  const count = items.length
  for (let i = 1; i <= count; i++) {
    const index = current === -1
      ? (step > 0 ? i - 1 : count - i)
      : (((current + step * i) % count) + count) % count
    if (!items[index].disabled) return index
  }
  return -1
}

// Typeahead for selects without a search field: first enabled option whose label starts with the buffer,
// searching after the current one so repeated presses of the same letter cycle through matches.
export function typeaheadIndex(items, buffer, current) {
  const needle = normalize(buffer)
  if (!needle) return -1

  const count = items.length
  const repeated = needle.split("").every(char => char === needle[0])
  const start = repeated ? current + 1 : Math.max(current, 0)
  const prefix = repeated ? needle[0] : needle
  for (let i = 0; i < count; i++) {
    const index = (start + i) % count
    if (!items[index].disabled && normalize(items[index].label).startsWith(prefix)) return index
  }
  return -1
}
